import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Sparkles, TrendingUp, Lock, Target, Brain } from "lucide-react";

export default function Analytics() {
    const { sessionId } = useParams();
    const nav = useNavigate();
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/analytics/session/${sessionId}`);
                setReport(r.data);
            } catch (e) {
                const detail = e?.response?.data?.detail;
                if (e?.response?.status === 402) setError({ paywall: true, ...detail });
                else setError({ message: (typeof detail === "string" ? detail : "AI analytics failed") });
            } finally { setLoading(false); }
        })();
    }, [sessionId]);

    if (loading) {
        return (
            <div className="max-w-3xl mx-auto py-16 px-6 text-center">
                <Brain className="w-10 h-10 text-primary mx-auto animate-pulse" />
                <p className="mt-4 font-mono text-sm text-muted-foreground">AI coach is analyzing your performance… (10-40s)</p>
            </div>
        );
    }

    if (error?.paywall) {
        return (
            <div className="max-w-3xl mx-auto py-16 px-6">
                <div className="border-2 border-primary rounded-sm p-10 bg-primary/5">
                    <Lock className="w-8 h-8 text-primary" />
                    <h1 className="mt-4 font-heading font-black text-3xl tracking-tighter">Free AI analytics used up</h1>
                    <p className="mt-3 text-muted-foreground">
                        You've used {error.used} of {error.limit} free AI diagnostic reports. Subscribe to Prepcheck Plus for unlimited AI analytics; everything else stays free.
                    </p>
                    <Link to="/subscription">
                        <Button data-testid="analytics-upgrade-btn" className="mt-6 rounded-sm bg-primary hover:bg-primary/90 btn-press">
                            <TrendingUp className="w-4 h-4 mr-2" /> Upgrade to Plus
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-3xl mx-auto py-16 px-6 text-center">
                <p className="text-primary font-mono">{error.message}</p>
                <Button className="mt-4 rounded-sm" onClick={() => nav(`/result/${sessionId}`)}>Back to scorecard</Button>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
            <span className="overline text-primary">▮ AI diagnostic</span>
            <h1 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tighter">
                Exam readiness · <span className="font-mono text-primary">{report.exam_readiness_score}<span className="text-2xl text-muted-foreground">/100</span></span>
            </h1>

            <div className="mt-8 grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-border rounded-sm p-6">
                    <span className="overline text-muted-foreground">Diagnosis</span>
                    <p className="mt-3 text-base leading-relaxed">{report.diagnosis}</p>

                    <div className="mt-6">
                        <div className="overline text-muted-foreground mb-2">Readiness</div>
                        <Progress value={report.exam_readiness_score} className="h-2 rounded-sm" />
                    </div>
                </div>

                <div className="border border-border rounded-sm p-6">
                    <span className="overline text-muted-foreground">Recommended topics</span>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {report.recommended_topics.map((t) => (
                            <span key={t} className="px-2 py-1 border border-primary/50 rounded-sm text-xs font-mono">
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-4">
                <div className="border border-border rounded-sm p-6">
                    <span className="overline text-green-600">Strengths</span>
                    <ul className="mt-3 space-y-2 text-sm">
                        {report.strengths.map((s, i) => (
                            <li key={i} data-testid={`strength-${i}`} className="flex items-start gap-2"><span className="text-green-600">▮</span>{s}</li>
                        ))}
                    </ul>
                </div>
                <div className="border border-border rounded-sm p-6">
                    <span className="overline text-primary">Weaknesses</span>
                    <ul className="mt-3 space-y-2 text-sm">
                        {report.weaknesses.map((s, i) => (
                            <li key={i} data-testid={`weakness-${i}`} className="flex items-start gap-2"><span className="text-primary">▮</span>{s}</li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="mt-8 border border-border rounded-sm p-6">
                <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    <h2 className="font-heading font-bold text-xl">7-day battle plan</h2>
                </div>
                <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-7 gap-3">
                    {report.study_plan.map((d, i) => (
                        <div key={i} data-testid={`study-day-${i}`} className="border border-border rounded-sm p-4 min-h-[8rem]">
                            <div className="overline text-primary">{d.day}</div>
                            <div className="text-sm font-medium mt-2 leading-tight">{d.focus}</div>
                            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                                {(d.tasks || []).map((t, j) => <li key={j}>• {t}</li>)}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
